import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { Tag } from '@shared/models/tag';
import { TagService, TagEntityType } from '@features/opportunities/services/tag.service';
import { TypeaheadOption } from '@shared/components/form-fields/typeahead-option';
import { SparkSharedFormTypeaheadFieldComponent } from '@shared/components/form-fields/typeahead/typeahead-field.component';

@Component({
  selector: 'app-tag-input',
  templateUrl: './tag-input.component.html',
  styleUrls: ['./tag-input.component.scss']
})
export class TagInputComponent implements OnInit, OnDestroy {
  @Input() entityId: string = '';
  @Input() entityType: TagEntityType = TagEntityType.Opportunities;
  @Input() taskId: string = '';
  @Input() initialTags: Tag[] | null = null;
  @Input() maxTags: number = 10;
  @Input() readonly: boolean = false;
  @Input() placeholder: string = 'Add tag...';
  @Input() showLabel: boolean = true;
  @Input() showEmptyState: boolean = true;
  @Input() label: string = 'Tags';

  @Output() tagsChanged = new EventEmitter<Tag[]>();
  @Output() tagAdded = new EventEmitter<Tag>();
  @Output() tagRemoved = new EventEmitter<Tag>();

  @ViewChild('typeaheadField', { static: false }) typeaheadField?: SparkSharedFormTypeaheadFieldComponent<Tag>;
  @ViewChild('typeaheadWrapper') typeaheadWrapper?: ElementRef<HTMLElement>;

  tags: Tag[] = [];
  showInput: boolean = false;
  isLoading: boolean = false;
  tagSearchControl = new FormControl<TypeaheadOption<Tag> | null>(null);
  
  tagDataSource$ = (query: string): Observable<TypeaheadOption<Tag>[]> => {
    return this.tagService.searchTags(query).pipe(
      map(tags => 
        tags
          .filter(tag => !this.tags.some(t => t.tagId === tag.tagId))
          .map(tag => ({
            label: tag.tagName,
            value: tag
          }))
      )
    );
  };

  private destroy$ = new Subject<void>();

  constructor(
    private tagService: TagService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.initialTags !== null) {
      this.tags = [...this.initialTags];
    } else if (this.entityId) {
      this.loadEntityTags();
    }
    this.setupTypeaheadListener();
  }

  private setupTypeaheadListener(): void {
    this.tagSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && value.value) {
          const selectedTag = value.value as Tag;
          this.onTagSelected(selectedTag);
        }
      });
  }

  private onTagSelected(tag: Tag): void {
    if (this.tags.some(t => t.tagId === tag.tagId)) {
      this.resetInput();
      return;
    }

    if (this.entityId) {
      this.isLoading = true;
      const add$ = this.taskId
        ? this.tagService.addTagToTask(this.entityType, this.entityId, this.taskId, { tagId: tag.tagId, tagName: tag.tagName })
        : this.tagService.addTagToEntity(this.entityType, this.entityId, { tagId: tag.tagId, tagName: tag.tagName });
      add$
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadEntityTags();
            this.tagAdded.emit(tag);
            this.resetInput();
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
            this.resetInput();
          }
        });
    } else {
      this.tags.push(tag);
      this.tagAdded.emit(tag);
      this.tagsChanged.emit(this.tags);
      this.resetInput();
    }
  }

  private resetInput(): void {
    this.tagSearchControl.setValue(null);
    if (this.typeaheadField) {
      this.typeaheadField.formGroup.controls.search.setValue('');
    }
    this.showInput = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadEntityTags(): void {
    if (this.entityId) {
      this.isLoading = true;
      const tags$ = this.taskId
        ? this.tagService.getTaskTags(this.entityType, this.entityId, this.taskId)
        : this.tagService.getEntityTags(this.entityType, this.entityId);
      tags$
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (tags) => {
            this.tags = tags;
            this.isLoading = false;
            this.tagsChanged.emit(this.tags);
            this.cdr.markForCheck();
          },
          error: () => {
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
    }
  }

  onEnterPressed(): void {
    const searchValue = this.typeaheadField?.formGroup.controls.search.value as string;
    const trimmedName = searchValue?.trim();
    if (!trimmedName) {
      this.resetInput();
      return;
    }

    if (this.tags.some(t => t.tagName.toLowerCase() === trimmedName.toLowerCase())) {
      this.resetInput();
      return;
    }

    if (this.entityId) {
      this.isLoading = true;
      this.tagService
        .getTagFromCacheOrCreate(trimmedName)
        .pipe(
          switchMap(tag => {
            const add$ = this.taskId
              ? this.tagService.addTagToTask(this.entityType, this.entityId, this.taskId, { tagId: tag.tagId, tagName: tag.tagName })
              : this.tagService.addTagToEntity(this.entityType, this.entityId, { tagId: tag.tagId, tagName: tag.tagName });
            return add$.pipe(map(() => tag));
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (tag) => {
            this.loadEntityTags();
            this.tagAdded.emit(tag);
            this.resetInput();
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
            this.resetInput();
          }
        });
    } else {
      this.isLoading = true;
      this.tagService
        .getTagFromCacheOrCreate(trimmedName)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (tag) => {
            if (!this.tags.some(t => t.tagId === tag.tagId)) {
              this.tags.push(tag);
              this.tagAdded.emit(tag);
              this.tagsChanged.emit(this.tags);
            }
            this.resetInput();
            this.isLoading = false;
          },
          error: () => {
            this.resetInput();
            this.isLoading = false;
          }
        });
    }
  }

  onFocusOut(): void {
    setTimeout(() => {
      if (!this.entityId) {
        this.onEnterPressed();
      } else {
        this.resetInput();
      }
    }, 200);
  }


  removeTag(tag: Tag, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.readonly) {
      return;
    }

    if (this.entityId) {
      const remove$ = this.taskId
        ? this.tagService.removeTagFromTask(this.entityType, this.entityId, this.taskId, tag.tagId)
        : this.tagService.removeTagFromEntity(this.entityType, this.entityId, tag.tagId);
      remove$
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadEntityTags();
            this.tagRemoved.emit(tag);
          },
          error: () => {
            this.cdr.markForCheck();
          }
        });
    } else {
      this.tags = this.tags.filter(t => t.tagId !== tag.tagId);
      this.tagRemoved.emit(tag);
      this.tagsChanged.emit(this.tags);
    }
  }

  toggleInputVisibility(): void {
    this.showInput = !this.showInput;
    this.cdr.markForCheck();
    if (this.showInput) {
      setTimeout(() => {
        const inputElement = this.typeaheadWrapper?.nativeElement.querySelector('input') as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }

  get canAddMoreTags(): boolean {
    return !this.readonly && this.tags.length < this.maxTags;
  }

  trackByTagId(index: number, tag: Tag): string {
    return tag.tagId;
  }
}

